import React, { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Redirect } from 'react-router-dom';
import i18next from 'i18next';

import ThresholdForm from './threshold_form';
import { PageTitle } from '../PageTitle';
import { Message } from '../Message';
import { TagsService, ThresholdsService, ThresholdEvaluationTypesService, DeviceDataTypesService, ThresholdClassificationsService, DeviceDataClassificationsService, DeviceDataOverrideService } from "../../services";

const loadConfigurationData = () => {
  return new Promise((resolve, reject) => {
    let requests = [];
    let errorMessages = [];
    let configurationData = {
      availableTags: null,
      availableDeviceDataTypes: null,
      availableThresholdEvaluationTypes: null,
      availableThresholdClassifications: null,
      availableDeviceDataClassifications: null
    };

    requests.push(
      TagsService.listAll().then(response => {
        configurationData.availableTags = response.tags.filter(tag => tag.tagType && (tag.tagType.token !== 'checklist-passive-device' && tag.tagType.token !== 'checklist-type'));
      }).catch(error => {
        errorMessages.push(i18next.t("messages.could-not-load-tags"));
        console.error(error);
      })
    );

    requests.push(
      DeviceDataTypesService.listAll().then(response => {
        configurationData.availableDeviceDataTypes = response.deviceDataTypes;
      }).catch(error => {
        errorMessages.push(i18next.t('messages.could-not-load-device-data-types'));
        console.error(error);
      })
    );

    requests.push(
      ThresholdEvaluationTypesService.getAll().then(response => {
        configurationData.availableThresholdEvaluationTypes = response.thresholdEvaluationTypes;
      }).catch(error => {
        errorMessages.push(i18next.t('messages.could-not-load-threshold-evaluation-types'));
        console.error(error);
      })
    );

    requests.push(
      ThresholdClassificationsService.getAll().then(response => {
        configurationData.availableThresholdClassifications = response.thresholdClassifications;
      }).catch(error => {
        errorMessages.push(i18next.t('messages.could-not-load-threshold-classifications'));
        console.error(error);
      })
    );

    requests.push(
      DeviceDataClassificationsService.listAll().then(response => {
        configurationData.availableDeviceDataClassifications = response.deviceDataClassifications;
      }).catch(error => {
        errorMessages.push("Could not load device data classifications.");
        console.error(error);
      })
    );

    Promise.all(requests).then(_ => {
      if (errorMessages.length > 0) {
        reject(errorMessages);      
      } else {
        resolve(configurationData);
      }
    });
  });
}

const ThresholdAdd = ({ location, me }) => {
  const [configurationData, setConfigurationData] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [addedSuccess, setAddedSuccess] = useState(false);

  useEffect(() => {
    loadConfigurationData()
      .then(setConfigurationData)
      .catch(errorMessages => {
        setErrorMessage(errorMessages.join(', '));
      });
  }, [me]);

  const handleSubmit = useCallback((thresholdData) => {
    const deviceDataTypeToken = thresholdData.deviceDataType?.deviceDataType?.token ?? thresholdData.deviceDataClassification?.deviceDataClassification?.token;
    const fnToApply = DeviceDataOverrideService.getReverseOverrideFunctionForDeviceTypeToken(deviceDataTypeToken);
    let evaluationNumericValue = null;
    if (thresholdData.thresholdEvaluationType.thresholdEvaluationType.valueType === "numeric") {
        evaluationNumericValue = fnToApply(parseFloat(thresholdData.thresholdEvaluationTypeValue));
    }

    const threshold = {
      name: thresholdData.name,
      description: thresholdData.description,
      enterprise: { id: me.enterprise.id },
      deviceDataType: thresholdData.deviceDataType?.deviceDataType ?? null,
      deviceDataClassification: thresholdData.deviceDataClassification?.deviceDataClassification ?? null,
      thresholdEvaluationType: { id: thresholdData.thresholdEvaluationType.thresholdEvaluationType.id },
      evaluationStringValue : thresholdData.thresholdEvaluationType.thresholdEvaluationType.valueType === 'string' ? thresholdData.thresholdEvaluationTypeValue : null,
      evaluationNumericValue : evaluationNumericValue,
      evaluationBooleanValue : thresholdData.thresholdEvaluationType.thresholdEvaluationType.valueType === 'boolean' ? thresholdData.thresholdEvaluationTypeValue.value : null,
      thresholdClassification: { id: thresholdData.thresholdClassification.thresholdClassification.id },
      tags: thresholdData.tags.map(tag => ({ id: tag.value }))
    };

    ThresholdsService.add(threshold).then(response => {
      setAddedSuccess(response.threshold != null);
    }).catch(error => {
      console.error(error);
      setErrorMessage(error.message);
    });
  }, [me]);

  if (errorMessage) {
    return (<Redirect to={{ pathname: '/config/thresholds', state: { errorMessage: errorMessage } }} />);
  } else if (addedSuccess) {
    return (<Redirect to={{ pathname: '/config/thresholds', state: { from: location, successMessage: i18next.t('messages.threshold-added') } }}/>);
  } else if (me && configurationData != null) {
    const backLinkWithParams = { link: { pathname: "/config/thresholds", search: location.state?.backLinkSearch } };

    return (
        <div>
          <div className="row">
            <PageTitle title={i18next.t('pages.thresholds.add-threshold')} backLinkWithParams={backLinkWithParams} />
          </div>

          <DisplayMessages errorMessage={errorMessage} onClearError={() => setErrorMessage(null)}/>

          <ThresholdForm threshold={null} configurationData={configurationData} onSubmit={handleSubmit} backLinkWithParams={backLinkWithParams} />
        </div>
    );
  } else {
    return (<div className="loader">{i18next.t('loading')}</div>);
  }
}

const DisplayMessages = ({errorMessage, onClearError}) => {
  return (
    <div>
      { errorMessage && <Message errorMessage={errorMessage} messageDismissCallback={onClearError} /> }
    </div>
  );
}

export default connect((state) => ({ me: state.user.me }))(ThresholdAdd);
